import type {
  BucketMovementStatus,
  BucketMovementType,
  ListBucketMovementsFilter,
  ListOnDemandRequestsFilter,
  OnDemandRequestStatus,
} from '@/integrations/agreements/client';
import { formatDay } from '@/utils/onDemandRequestRules';

/**
 * Filtros das listagens do fluxo novo (coletas e movimentações de equipamento).
 *
 * Só o que o musa-agreements já filtra no servidor — filtrar a página na tela deixaria a
 * paginação mentindo. Por isso a coleta tem menos filtros que a movimentação, e estabelecimento,
 * resíduo e acondicionamento aceitam um valor só.
 */
export interface OnDemandListFilters {
  /**
   * Gerador (N1), pelo `external_id`. Só estreita a lista de estabelecimentos: a API filtra por
   * um gerador exato, sem a árvore, então ele sozinho não filtra a listagem.
   */
  generatorId: string | null;
  /** Estabelecimento (id numérico) — é o `generatorId` da API. */
  establishmentId: string | null;
  /** Só na movimentação. */
  materialId: string | null;
  /** Só na movimentação. */
  packagingId: string | null;
  /** Coleta: um status só. Movimentação: vários (OU). */
  statuses: number[];
  /** Só na movimentação: colocação, retirada, troca (OU). */
  types: number[];
  /** `YYYY-MM-DD`; vazio = sem limite. */
  dateFrom: string;
  dateTo: string;
}

export const EMPTY_ON_DEMAND_LIST_FILTERS: OnDemandListFilters = {
  generatorId: null,
  establishmentId: null,
  materialId: null,
  packagingId: null,
  statuses: [],
  types: [],
  dateFrom: '',
  dateTo: '',
};

export type OnDemandFilterKey =
  | 'gerador'
  | 'estabelecimento'
  | 'residuo'
  | 'acondicionamento'
  | 'status'
  | 'operacao'
  | 'data';

const toId = (value: string | null) => (value ? Number(value) : undefined);

export function toRequestsFilter(filters: OnDemandListFilters): Omit<ListOnDemandRequestsFilter, 'page' | 'pageSize'> {
  return {
    status: filters.statuses[0] as OnDemandRequestStatus | undefined,
    generatorId: toId(filters.establishmentId),
    plannedDateFrom: filters.dateFrom || undefined,
    plannedDateTo: filters.dateTo || undefined,
  };
}

export function toMovementsFilter(filters: OnDemandListFilters): Omit<ListBucketMovementsFilter, 'page' | 'pageSize'> {
  return {
    status: filters.statuses.length ? (filters.statuses as BucketMovementStatus[]) : undefined,
    type: filters.types.length ? (filters.types as BucketMovementType[]) : undefined,
    generatorId: toId(filters.establishmentId),
    materialId: toId(filters.materialId),
    packagingId: toId(filters.packagingId),
    plannedFrom: filters.dateFrom || undefined,
    plannedTo: filters.dateTo || undefined,
  };
}

/** Algum filtro chega ao serviço — o gerador sozinho não chega (ver `generatorId`). */
export function hasAppliedFilters(filters: OnDemandListFilters): boolean {
  return Boolean(
    filters.establishmentId
      || filters.materialId
      || filters.packagingId
      || filters.statuses.length
      || filters.types.length
      || filters.dateFrom
      || filters.dateTo,
  );
}

/** Gerador escolhido sem estabelecimento: a lista ainda não está filtrada por ele. */
export function isGeneratorPending(filters: OnDemandListFilters): boolean {
  return Boolean(filters.generatorId && !filters.establishmentId);
}

export interface OnDemandFilterChip {
  key: OnDemandFilterKey;
  value?: string;
  label: string;
  display: string;
}

/** Nome legível de cada valor escolhido, por filtro. */
export type OnDemandFilterLabels = Partial<Record<OnDemandFilterKey, Record<string, string>>>;

const labelOf = (labels: OnDemandFilterLabels, key: OnDemandFilterKey, value: string) =>
  labels[key]?.[value] ?? value;

export function buildFilterChips(filters: OnDemandListFilters, labels: OnDemandFilterLabels): OnDemandFilterChip[] {
  const chips: OnDemandFilterChip[] = [];
  const single = (key: OnDemandFilterKey, label: string, value: string | null) => {
    if (value) chips.push({ key, label, display: labelOf(labels, key, value) });
  };

  single('gerador', 'Gerador', filters.generatorId);
  single('estabelecimento', 'Estabelecimento', filters.establishmentId);
  single('residuo', 'Resíduo', filters.materialId);
  single('acondicionamento', 'Acondicionamento', filters.packagingId);

  filters.statuses.forEach((status) => {
    chips.push({ key: 'status', value: String(status), label: 'Status', display: labelOf(labels, 'status', String(status)) });
  });
  filters.types.forEach((type) => {
    chips.push({
      key: 'operacao',
      value: String(type),
      label: 'Tipo de operação',
      display: labelOf(labels, 'operacao', String(type)),
    });
  });

  if (filters.dateFrom) chips.push({ key: 'data', value: 'de', label: 'Data de', display: formatDay(filters.dateFrom) });
  if (filters.dateTo) chips.push({ key: 'data', value: 'ate', label: 'Data até', display: formatDay(filters.dateTo) });

  return chips;
}

/** Tira um valor (chip) ou o filtro inteiro (sem `value`). */
export function removeFilter(filters: OnDemandListFilters, key: OnDemandFilterKey, value?: string): OnDemandListFilters {
  switch (key) {
    case 'gerador':
      return { ...filters, generatorId: null };
    case 'estabelecimento':
      return { ...filters, establishmentId: null };
    case 'residuo':
      return { ...filters, materialId: null };
    case 'acondicionamento':
      return { ...filters, packagingId: null };
    case 'status':
      return { ...filters, statuses: value ? filters.statuses.filter((s) => String(s) !== value) : [] };
    case 'operacao':
      return { ...filters, types: value ? filters.types.filter((t) => String(t) !== value) : [] };
    case 'data':
      return {
        ...filters,
        dateFrom: value === 'ate' ? filters.dateFrom : '',
        dateTo: value === 'de' ? filters.dateTo : '',
      };
  }
}

/** "Outros" sempre no fim, o resto em ordem alfabética — igual à tela anterior. */
export function sortOptionsWithOthersLast<T extends { label: string }>(options: T[]): T[] {
  return [...options].sort((a, b) => {
    if (a.label.toLowerCase() === 'outros') return 1;
    if (b.label.toLowerCase() === 'outros') return -1;
    return a.label.localeCompare(b.label, 'pt-BR');
  });
}
